// One shared 1-second clock for every live timer on screen (board cards). A single
// setInterval runs only while at least one component subscribes AND the tab is visible;
// hidden tabs stop ticking and resync immediately when shown again.
import { useSyncExternalStore } from "react";

const TICK_MS = 1000;
const listeners = new Set<() => void>();
let now = Date.now();
let timer: number | null = null;
let visibilityBound = false;

function emit(): void {
  now = Date.now();
  for (const l of listeners) l();
}

function isHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function stop(): void {
  if (timer !== null) {
    window.clearInterval(timer);
    timer = null;
  }
}

function start(): void {
  if (timer !== null || listeners.size === 0 || isHidden()) return;
  timer = window.setInterval(emit, TICK_MS);
}

function onVisibility(): void {
  if (isHidden()) stop();
  else {
    emit();
    start();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!visibilityBound && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
    visibilityBound = true;
  }
  if (listeners.size === 1) now = Date.now();
  start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stop();
      if (visibilityBound) {
        document.removeEventListener("visibilitychange", onVisibility);
        visibilityBound = false;
      }
    }
  };
}

function snapshot(): number {
  // Before the first subscriber (or after the last one left) the stored time may be stale:
  // refresh it, but at most once per tick so consecutive reads stay identical.
  if (listeners.size === 0 && Date.now() - now >= TICK_MS) now = Date.now();
  return now;
}

/** Current time (ms), re-rendering the caller once per second through the shared clock. */
export function useTickingNow(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
